import {
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, CardBody, CardHeader } from '@heroui/react';
import { Input } from '@heroui/input';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import OrderBoard from '../../components/OrderBoard/OrderBoard';
import CreateCustomBoardModal from '../../components/OrderBoard/CreateCustomBoardModal';
import CreateCardModal from '../../components/OrderBoard/CreateCardModal';
import ImportEtsyModal from '../../components/OrderBoard/ImportEtsyModal';
import { orderBoardService } from '../../services/orderBoardService';
import {
  CreateCustomBoardRequest,
  CreateCardRequest,
  ImportEtsyOrdersRequest,
} from '../../types/orderBoard';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const OrderBoards: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  const [isCreateBoardOpen, setIsCreateBoardOpen] = useState(false);
  const [isImportEtsyOpen, setIsImportEtsyOpen] = useState(false);
  const [isCreateCardOpen, setIsCreateCardOpen] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: boardsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['orderBoards'],
    queryFn: () => orderBoardService.getAllBoards(),
  });

  const { data: selectedBoard, isLoading: isLoadingBoard } = useQuery({
    queryKey: ['orderBoard', selectedBoardId],
    queryFn: () => orderBoardService.getBoardById(selectedBoardId!),
    enabled: selectedBoardId !== null,
  });

  const createBoardMutation = useMutation({
    mutationFn: (request: CreateCustomBoardRequest) =>
      orderBoardService.createCustomBoard(request),
    onSuccess: board => {
      toast.success('Board created successfully');
      queryClient.invalidateQueries({ queryKey: ['orderBoards'] });
      setSelectedBoardId(board.id);
      setIsCreateBoardOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create board');
    },
  });

  const importEtsyMutation = useMutation({
    mutationFn: (request: ImportEtsyOrdersRequest) =>
      orderBoardService.importEtsyOrders(request),
    onSuccess: boards => {
      toast.success(`Imported ${boards.length} Etsy orders`);
      queryClient.invalidateQueries({ queryKey: ['orderBoards'] });
      setIsImportEtsyOpen(false);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || 'Failed to import Etsy orders'
      );
    },
  });

  const createCardMutation = useMutation({
    mutationFn: (request: CreateCardRequest) =>
      orderBoardService.createCard(request),
    onSuccess: () => {
      toast.success('Card created successfully');
      queryClient.invalidateQueries({
        queryKey: ['orderBoard', selectedBoardId],
      });
      queryClient.invalidateQueries({ queryKey: ['orderBoards'] });
      setIsCreateCardOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create card');
    },
  });

  const filteredBoards =
    boardsData?.boards.filter(
      board =>
        board.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        board.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        board.productName?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const handleBoardSelect = (boardId: number) => {
    setSelectedBoardId(boardId);
  };

  const handleBoardUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['orderBoards'] });
    if (selectedBoardId) {
      queryClient.invalidateQueries({
        queryKey: ['orderBoard', selectedBoardId],
      });
    }
  };

  if (error) {
    toast.error('Failed to load boards');
  }

  if (selectedBoard && selectedBoardId) {
    return (
      <div className='space-y-6'>
        <SEOHead
          title='Order Board'
          description='Manage order workflow using Kanban board.'
          url={generateCanonicalUrl('/admin/boards')}
          type='website'
        />
        <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'>
          <div className='flex-1 min-w-0'>
            <Button
              variant='ghost'
              onClick={() => setSelectedBoardId(null)}
              className='mb-2 sm:mb-4 text-sm'
            >
              ← Back to Boards
            </Button>
            <h2 className='text-xl sm:text-2xl font-bold truncate'>
              {selectedBoard.title}
            </h2>
            <p className='text-xs sm:text-sm text-gray-500'>
              {selectedBoard.isCustom
                ? 'Custom Board'
                : `Order #${selectedBoard.orderNumber}`}
            </p>
            {selectedBoard.productName && (
              <p className='text-xs sm:text-sm text-gray-600 mt-1'>
                Product: {selectedBoard.productName}
              </p>
            )}
            {selectedBoard.isLocked && (
              <p className='text-xs sm:text-sm text-red-600 mt-1 font-semibold'>
                🔒 Board Locked
              </p>
            )}
            {selectedBoard.description && (
              <p className='text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2'>
                {selectedBoard.description}
              </p>
            )}
          </div>
          <Button
            color='primary'
            startContent={<PlusIcon className='h-4 w-4' />}
            onClick={() => setIsCreateCardOpen(true)}
            className='w-full sm:w-auto flex-shrink-0'
          >
            Add Card
          </Button>
        </div>

        {isLoadingBoard ? (
          <div className='text-center py-12'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto' />
            <p className='mt-2 text-sm text-gray-500'>Loading board...</p>
          </div>
        ) : (
          <div className='bg-gray-50 p-2 sm:p-4 md:p-6 rounded-lg border border-gray-200 -mx-2 sm:mx-0'>
            <OrderBoard
              board={selectedBoard}
              onBoardUpdate={handleBoardUpdate}
            />
          </div>
        )}

        <CreateCardModal
          isOpen={isCreateCardOpen}
          onClose={() => setIsCreateCardOpen(false)}
          onConfirm={async request => {
            await createCardMutation.mutateAsync(request);
          }}
          boardId={selectedBoardId}
          columns={selectedBoard.columns}
        />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Order Boards'
        description='Manage and track customer orders using Kanban boards.'
        url={generateCanonicalUrl('/admin/boards')}
        type='website'
      />

      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <h2 className='text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate'>
            Order Boards
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            Manage order workflow using Trello-style Kanban boards
          </p>
        </div>
        <div className='mt-4 flex gap-2 md:mt-0 md:ml-4'>
          <Button
            color='secondary'
            variant='flat'
            startContent={<ArrowDownTrayIcon className='h-4 w-4' />}
            onClick={() => setIsImportEtsyOpen(true)}
          >
            Import Etsy
          </Button>
          <Button
            color='primary'
            startContent={<PlusIcon className='h-4 w-4' />}
            onClick={() => setIsCreateBoardOpen(true)}
          >
            Create Board
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className='flex items-center gap-4 w-full'>
            <Input
              placeholder='Search by order number or title...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              startContent={
                <MagnifyingGlassIcon className='h-4 w-4 text-gray-400' />
              }
              className='flex-1'
            />
          </div>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className='text-center py-12'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto' />
            </div>
          ) : filteredBoards.length === 0 ? (
            <div className='text-center py-12 text-gray-500'>
              {searchQuery
                ? 'No boards found matching your search'
                : 'No order boards found. Create a custom board or wait for orders to be placed.'}
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {filteredBoards.map(board => (
                <div
                  key={board.id}
                  className='cursor-pointer'
                  onClick={() => handleBoardSelect(board.id)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBoardSelect(board.id);
                    }
                  }}
                >
                  <Card className='h-full hover:shadow-lg transition-shadow'>
                    <CardBody className='p-4'>
                      <div className='flex items-start justify-between'>
                        <div className='flex-1'>
                          <div className='flex items-center gap-2'>
                            <h3 className='font-semibold text-lg text-gray-900'>
                              {board.title}
                            </h3>
                            {board.isCustom && (
                              <span className='text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded'>
                                Custom
                              </span>
                            )}
                          </div>
                          {board.orderNumber && (
                            <p className='text-sm text-gray-500 mt-1'>
                              Order #{board.orderNumber}
                            </p>
                          )}
                          {board.productName && (
                            <p className='text-sm text-gray-600 mt-1 font-medium'>
                              {board.productName}
                            </p>
                          )}
                          {board.isLocked && (
                            <span className='inline-flex items-center gap-1 text-xs text-red-600 mt-1 font-semibold'>
                              🔒 Locked
                            </span>
                          )}
                          {board.description && (
                            <p className='text-xs text-gray-600 mt-1 line-clamp-2'>
                              {board.description}
                            </p>
                          )}
                          <p className='text-xs text-gray-400 mt-2'>
                            Created:{' '}
                            {new Date(board.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className='mt-4 flex items-center gap-2'>
                        <span className='text-xs text-gray-500'>
                          {board.columns.reduce(
                            (sum, col) => sum + col.cards.length,
                            0
                          )}{' '}
                          cards
                        </span>
                        <span className='text-gray-300'>•</span>
                        <span className='text-xs text-gray-500'>
                          {board.columns.length} columns
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <CreateCustomBoardModal
        isOpen={isCreateBoardOpen}
        onClose={() => setIsCreateBoardOpen(false)}
        onConfirm={async request => {
          await createBoardMutation.mutateAsync(request);
        }}
      />

      <ImportEtsyModal
        isOpen={isImportEtsyOpen}
        onClose={() => setIsImportEtsyOpen(false)}
        onConfirm={async request => {
          await importEtsyMutation.mutateAsync(request);
        }}
      />
    </div>
  );
};

export default OrderBoards;
